import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

interface ClientRecord {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'inactive' | 'suspended';
}

interface ClientConfigCardProps {
  client: ClientRecord | null;
  isLoading: boolean;
  onNameUpdated: (name: string) => void;
  className?: string;
}

const statusVariant: Record<ClientRecord['status'], string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-700',
  suspended: 'bg-red-100 text-red-700',
};

export function ClientConfigCard({
  client,
  isLoading,
  onNameUpdated,
  className,
}: ClientConfigCardProps) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleEdit = () => {
    setName(client?.name ?? '');
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setName('');
  };

  const handleSave = async () => {
    if (!client || !name.trim()) return;
    setSaving(true);
    const { error } = await supabase
      .from('clients')
      .update({ name: name.trim() })
      .eq('id', client.id);
    setSaving(false);
    if (error) {
      toast.error('Failed to save settings. Please check your connection and try again.');
      return;
    }
    toast.success('Settings saved successfully.');
    onNameUpdated(name.trim());
    setEditing(false);
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-4 w-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Client Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="client-name">Organization Name</Label>
          {editing ? (
            <div className="flex gap-2">
              <Input
                id="client-name"
                value={name}
                onChange={(e) => { setName(e.target.value); }}
                maxLength={200}
                autoFocus
                aria-label="Organization name"
              />
              <Button size="sm" onClick={() => { void handleSave(); }} disabled={saving || !name.trim()}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">{client?.name ?? '—'}</span>
              <Button size="sm" variant="outline" onClick={handleEdit}>Edit</Button>
            </div>
          )}
        </div>
        <div className="space-y-1">
          <Label>Slug</Label>
          <p className="text-sm text-muted-foreground font-mono">{client?.slug ?? '—'}</p>
        </div>
        <div className="space-y-1">
          <Label>Status</Label>
          <div>
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize',
                client ? statusVariant[client.status] : 'bg-gray-100 text-gray-700',
              )}
            >
              {client?.status ?? '—'}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
